import ClientReportSharing from './ClientReportSharing';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaClock, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import { appConfirmMessage } from '../../../utils/appConfirm';
import { patchAdminSearch } from '../../../utils/adminDashboardNav';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import {
  collectTeamMembers,
  computeNextService,
  computeProgress,
  formatDate,
  mapStatusToDisplay,
  statusDetailClass,
  resolveProjectDescription,
  formatPipelineLabel,
  resolveProjectFrequency,
  formatRecurrenceFrequencyLabel,
  resolveProjectServices,
  resolveProjectServiceNames,
  resolveProjectDisplayTotal,
} from '../utils/projectUtils';
import TaskAssigneePicker from '../../../components/TaskAssigneePicker';
import {
  applyTaskAssignmentToInput,
  collectTaskAssigneeIds,
  formatTaskAssigneeNames,
  mapTaskStatusToDisplay,
  taskAssigneesWereLoaded,
} from '../../../utils/taskDisplay';
import {
  ProfileBookingProvider,
  useProfileRefresh,
} from '../../ProfilePages/ProfileBookingContext';
import History from '../../ProfilePages/components/History';
import Invoice from '../../ProfilePages/components/Invoice';
import Files from '../../ProfilePages/components/Files';
import CustomInvoicePanel from './CustomInvoicePanel';
import { currentBillingPeriod } from '../../../utils/projectFrequency';
import '../styles/ProjectDetailView.css';
import InvoiceSettingsDrawer from './InvoiceSettingDrawer';
import useProjectInvoiceSettings from './useProjectInvoiceSettings';
import '../styles/InvoiceSettingDrawer.css';

const FIND_ONE_PROJECT = `
  query FindOneProject($id: String!) {
    findOneProject(id: $id) {
      id
      projectId
      name
      startDate
      deadLine
      deadlineOngoing
      startTime
      endTime
      status
      createdAt
      recurrenceFrequency
      recurrenceActive
      nextVisitAt
      seriesRootId
      purchaseOrderNumber
      projectAddress
      deliveryAddress
      attention
      billingCadence
      autoSendMonthlyInvoice
      invoiceLineMode
      booking {
        id
        fullName
        email
        phone
        address
        postcode
        bookingFrequency
        message
        preferredDate
        estimatedPRice
        status
        stage {
          id
          name
          order
          pipeline {
            id
            name
          }
        }
        bookingServices {
          id
          frequency
          price
          total
          service {
            id
            service
          }
        }
      }
      manager {
        id
        firstName
        lastName
        email
      }
      tasks {
        id
        title
        description
        remainder
        status
        date
        priority
        user {
          id
          firstName
          lastName
        }
        assignees {
          id
          firstName
          lastName
        }
      }
      quotedServices
      quotedTotal
      projectServices
    }
  }
`;

const PIPELINE_STAGES = `
  query FindAllStagesOfGivenPipeline($id: String!) {
    findAllStagesOfGivenPipeline(id: $id) {
      id
      name
      order
    }
  }
`;

const CREATE_TASK = `
  mutation CreateTask($input: CreateTaskInput!) {
    createTask(createTaskInput: $input) {
      id
      title
      status
      user {
        id
        firstName
        lastName
      }
      assignees {
        id
        firstName
        lastName
      }
    }
  }
`;

const FIND_TASKS_BY_PROJECT = `
  query FindTasksByProject($projectId: String!) {
    findTasksByProject(projectId: $projectId) {
      id
      title
      description
      remainder
      status
      date
      priority
      user {
        id
        firstName
        lastName
        email
      }
      assignees {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

const UPDATE_TASK = `
  mutation UpdateTask($input: UpdateTaskInput!) {
    updateTask(updateTaskInput: $input) {
      id
      title
      description
    }
  }
`;

const GET_ASSIGNABLE_USERS = `
  query GetAllUser($page: Int, $limit: Int, $roles: [String!]) {
    getAllUser(page: $page, limit: $limit, roles: $roles) {
      data { id firstName lastName email }
    }
  }
`;

const DELETE_TASK = `
  mutation RemoveTask($id: String!) {
    removeTask(id: $id) {
      id
    }
  }
`;

const GENERATE_MONTHLY_INVOICE = `
  mutation GenerateMonthlyProjectInvoice(
    $projectId: String!
    $billingPeriod: String
    $send: Boolean
  ) {
    generateMonthlyProjectInvoice(
      projectId: $projectId
      billingPeriod: $billingPeriod
      send: $send
    ) {
      id
      invoiceNumber
      invoiceStage
    }
  }
`;
const TABS = ['Overview', 'Tasks', 'Team', 'Files', 'Invoices', 'Client sharing', 'Activity'];

const emptyTaskForm = (serviceId = '') => ({
  title: '',
  scope: '',
  instructions: '',
  priority: '',
  date: '',
  userId: '',
  assigneeIds: [],
  serviceId: serviceId || '',
  groupName: '',
  groupPick: '',
});

function toDateInputValue(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function projectTaskCreateDefaults(project) {
  return {
    date:
      toDateInputValue(project?.startDate) ||
      toDateInputValue(project?.deadLine) ||
      '',
    userId: project?.manager?.id || '',
  };
}

function personName(user) {
  if (!user) return '';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || '';
}

function taskManagerLabel(task, project) {
  const user = task?.user;
  if (!user?.id) return '';
  const name = personName(user);
  if (!name) return '';
  if (project?.manager?.id && user.id === project.manager.id) {
    return `Manager: ${name}`;
  }
  return name;
}

function catalogServiceKeyFromTask(task) {
  const remainder = String(task?.remainder || '').trim();
  if (remainder.startsWith('svc:')) return remainder.slice(4);
  const desc = String(task?.description || '');
  const idLine = desc.match(/^Service:([0-9a-f-]{36})\s*$/im);
  if (idLine) return idLine[1];
  return '';
}

function displayTaskDescription(description) {
  return String(description || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(
      (line) =>
        line &&
        !/^Service:/i.test(line) &&
        !/^Source:/i.test(line) &&
        !/^Group:/i.test(line) &&
        !/^Client-facing scope:/i.test(line) &&
        !/^Internal work instructions:/i.test(line) &&
        !/^Requirement:/i.test(line) &&
        !/^Role:/i.test(line) &&
        !/^Frequency:/i.test(line),
    )
    .join('\n');
}

function taskDescriptionPoints(description) {
  const notes = displayTaskDescription(description);
  let points = String(notes || '')
    .split(/\n+/)
    .map((line) =>
      line
        .replace(/^[-*•]+\s*/, '')
        .replace(/^\d+[.)]\s*/, '')
        .trim(),
    )
    .filter(Boolean);
  if (points.length <= 1) {
    const blob = points[0] || '';
    const sentences = blob
      .split(/(?<=\.)\s+(?=[A-Z*•-])/)
      .map((part) => part.replace(/^[-*•]+\s*/, '').trim())
      .filter(Boolean);
    if (sentences.length > 1) points = sentences;
  }
  return points;
}

function labeledLine(description, label) {
  const match = String(description || '').match(
    new RegExp(`^${label}:\\s*(.+)$`, 'im'),
  );
  return String(match?.[1] || '').trim();
}

function parseTaskMeta(task) {
  const description = String(task?.description || '');
  const instructions = labeledLine(description, 'Internal work instructions');
  const scope =
    labeledLine(description, 'Client-facing scope') ||
    (instructions ? '' : displayTaskDescription(description));
  return {
    serviceId: catalogServiceKeyFromTask(task),
    groupName: String(task?.groupName || labeledLine(description, 'Group')).trim(),
    scope,
    instructions,
  };
}

function groupNamesForService(project, tasks, serviceId) {
  const names = [];
  const add = (value) => {
    const name = String(value || '').trim();
    if (!name || name.toLowerCase() === 'tasks') return;
    if (names.some((item) => item.toLowerCase() === name.toLowerCase())) return;
    names.push(name);
  };
  resolveProjectServices(project).forEach((row) => {
    const id = String(row?.service?.id || row?.catalogServiceId || '').trim();
    if (serviceId && id && id !== serviceId) return;
    (row.tasks || []).forEach((task) =>
      add(task?.groupName || labeledLine(task?.description, 'Group')),
    );
  });
  (tasks || []).forEach((task) => {
    const key = catalogServiceKeyFromTask(task);
    if (serviceId && key && key !== serviceId) return;
    add(task?.groupName || labeledLine(task?.description, 'Group'));
  });
  return names;
}

function chosenGroupName(form) {
  return String(form?.groupName || '').trim();
}

function uniqueTasks(rows) {
  const seen = new Set();
  const out = [];
  for (const task of rows || []) {
    const id = String(task?.id || '');
    const content = [
      catalogServiceKeyFromTask(task),
      String(task?.title || '').trim().toLowerCase(),
      displayTaskDescription(task?.description).trim().toLowerCase(),
    ].join('|');
    if (id && seen.has(`id:${id}`)) continue;
    if (seen.has(`c:${content}`)) continue;
    if (id) seen.add(`id:${id}`);
    seen.add(`c:${content}`);
    out.push(task);
  }
  return out;
}

function projectServiceRows(project) {
  const rows = resolveProjectServices(project);
  return rows
    .map((row, index) => {
      const id = String(
        row?.service?.id || row?.catalogServiceId || row?.id || '',
      ).trim();
      const name = String(
        row?.serviceName || row?.name || row?.service?.service || '',
      ).trim();
      if (!id && !name) return null;
      return {
        catalogId: String(row?.service?.id || row?.catalogServiceId || id).trim(),
        name: name || `Service ${index + 1}`,
      };
    })
    .filter(Boolean);
}

function buildTaskDescription({ serviceId, groupName, scope, instructions }) {
  const scopeText = String(scope || '').trim();
  const instructionText = String(instructions || '').trim();
  const group = String(groupName || '').trim();
  return [
    'Source: project',
    serviceId ? `Service:${serviceId}` : null,
    group ? `Group: ${group}` : null,
    scopeText ? `Client-facing scope: ${scopeText}` : null,
    instructionText ? `Internal work instructions: ${instructionText}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

function ProjectFilesPanel({ bookingId, projectId }) {
  const { bumpRefresh } = useProfileRefresh();

  useEffect(() => {
    if (!bookingId) return;
    try {
      Cookies.set('bookingId', bookingId);
    } catch {
      /* ignore */
    }
    bumpRefresh();
  }, [bookingId, bumpRefresh]);

  if (!bookingId) {
    return (
      <div className="pd-card pd-card--full pd-embedded-tab">
        <h3>Files</h3>
        <p className="pd-muted">No linked deal — files unavailable.</p>
      </div>
    );
  }

  return (
    <div className="pd-card pd-card--full pd-embedded-tab pd-files-panel">
      <Files bookingId={bookingId} projectId={projectId} />
    </div>
  );
}

export default function ProjectDetailView({
  projectId,
  onBack,
  initialTab = 'Overview',
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [pipelineName, setPipelineName] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab || 'Overview');
  const [showAddTask, setShowAddTask] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const savingTaskRef = useRef(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState(emptyTaskForm());
  const [managers, setManagers] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [generatingMonthlyInvoice, setGeneratingMonthlyInvoice] =
    useState(false);
  const { bumpRefresh } = useProfileRefresh();

  useEffect(() => {
    setActiveTab(initialTab || 'Overview');
  }, [initialTab, projectId]);

  const selectTab = (tab) => {
    const nextTab = TABS.includes(tab) ? tab : 'Overview';
    setActiveTab(nextTab);
    const search = patchAdminSearch(
      location.search,
      { tab: nextTab },
      nextTab === 'Invoices' ? [] : ['invoice'],
    );
    if (search !== (location.search || '')) {
      navigate(
        { pathname: location.pathname || '/', search },
        { replace: true },
      );
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setProjectTasks([]);
        setProject((prev) => (prev?.id === projectId ? prev : null));
        const data = await graphqlRequest(FIND_ONE_PROJECT, { id: projectId });
        const proj = data?.findOneProject || null;
        if (cancelled) return;
        setProject(proj);
        if (proj?.id) {
          graphqlRequest(FIND_TASKS_BY_PROJECT, { projectId: proj.id })
            .then((taskRes) => {
              if (cancelled) return;
              setProjectTasks(
                Array.isArray(taskRes?.findTasksByProject)
                  ? taskRes.findTasksByProject
                  : [],
              );
            })
            .catch(() => {
              if (!cancelled) setProjectTasks([]);
            });
        }
        const bid = proj?.booking?.id;
        if (bid) {
          try {
            Cookies.set('bookingId', bid);
          } catch {
            /* ignore */
          }
        }
        setLoading(false);

        // Pipeline stages are decorative — load after first paint, don't block Overview.
        const pipelineId = proj?.booking?.stage?.pipeline?.id;
        const pipelineLabel = proj?.booking?.stage?.pipeline?.name || '';
        setPipelineName(pipelineLabel);
        if (!pipelineId) {
          setPipelineStages([]);
          return;
        }
        const stagesData = await graphqlRequest(PIPELINE_STAGES, {
          id: pipelineId,
        });
        if (cancelled) return;
        const stages = (stagesData?.findAllStagesOfGivenPipeline || []).slice();
        stages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setPipelineStages(stages);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        toast.error(err?.message || 'Failed to load project');
        setProject(null);
        setPipelineStages([]);
        setLoading(false);
      }
    };
    if (projectId) load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const displayStatus = useMemo(
    () => (project ? mapStatusToDisplay(project.status) : ''),
    [project],
  );

  const progress = useMemo(
    () => (project ? computeProgress(project) : { percent: 0, done: 0, total: 0 }),
    [project],
  );

  const nextService = useMemo(
    () => (project ? computeNextService(project) : 'Not scheduled'),
    [project],
  );

  const team = useMemo(
    () => (project ? collectTeamMembers(project) : []),
    [project],
  );

  const booking = project?.booking;
  const bookingId = booking?.id;

  const reloadProject = async () => {
    if (!project?.id) return;
    const data = await graphqlRequest(FIND_ONE_PROJECT, { id: project.id });
    setProject(data?.findOneProject || project);
    bumpRefresh();
  };

  const reloadTasks = async () => {
    if (!project?.id) return;
    const taskRes = await graphqlRequest(FIND_TASKS_BY_PROJECT, {
      projectId: project.id,
    }).catch(() => ({}));
    setProjectTasks(
      uniqueTasks(
        Array.isArray(taskRes?.findTasksByProject) ? taskRes.findTasksByProject : [],
      ),
    );
  };

  // People a task can be assigned to (the form's "Assigned to").
  useEffect(() => {
    if (!showAddTask) return;
    graphqlRequest(GET_ASSIGNABLE_USERS, {
      page: 1,
      limit: 100,
      // Role filter matches normalised names: "Field Cleaner" → FIELD_CLEANER.
      roles: [
        'OPERATIONS_MANAGER',
        'SUPERVISOR',
        'SUPER_ADMIN',
        'ADMIN',
        'BRANCH_ADMIN',
        'FIELD_CLEANER',
        'CLEANER',
      ],
    })
      .then((res) => setManagers(res?.getAllUser?.data || []))
      .catch(() => setManagers([]));
  }, [showAddTask]);

  const [showInvoiceSettings, setShowInvoiceSettings] = useState(false);
  const invoiceSettings = useProjectInvoiceSettings(project?.id);

  const handleSaveInvoiceSettings = async (settings) => {
    if (await invoiceSettings.save(settings)) {
      setShowInvoiceSettings(false);
      await reloadProject();
    }
  };

  const invoiceSchedule = invoiceSettings.summary
    ? {
        autoInvoiceIds: invoiceSettings.autoInvoiceIds,
        upcoming:
          invoiceSettings.summary.recurring && invoiceSettings.summary.nextInvoice
            ? {
                date: invoiceSettings.summary.nextInvoice,
                amount: invoiceSettings.summary.nextAmount,
              }
            : null,
        onManage: () => setShowInvoiceSettings(true),
      }
    : null;

  const openAddTask = () => {
    const serviceRows = projectServiceRows(project);
    setEditingTask(null);
    setTaskForm(emptyTaskForm(serviceRows[0]?.catalogId || ''));
    setShowAddTask(true);
  };

  const openEditTask = (task) => {
    if (String(task?.id || '').startsWith('snap-')) return;
    const meta = parseTaskMeta(task);
    const serviceRows = projectServiceRows(project);
    const assigneeIds = collectTaskAssigneeIds(task);
    setEditingTask(task);
    setTaskForm({
      ...emptyTaskForm(meta.serviceId || serviceRows[0]?.catalogId || ''),
      title: task.title || '',
      groupName: meta.groupName,
      scope: meta.scope,
      instructions: meta.instructions,
      priority: task.priority || '',
      date: toDateInputValue(task.date),
      userId: assigneeIds[0] || '',
      assigneeIds,
      assigneesLoaded: taskAssigneesWereLoaded(task),
    });
    setShowAddTask(true);
  };

  const closeAddTask = () => {
    if (savingTask) return;
    setShowAddTask(false);
    setEditingTask(null);
    setTaskForm(emptyTaskForm());
  };

  const handleDeleteTask = async (id) => {
    if (String(id || '').startsWith('snap-')) return;
    if (!(await appConfirmMessage('Delete this task?'))) return;
    try {
      await graphqlRequest(DELETE_TASK, { id });
      toast.success('Task deleted');
      await Promise.all([reloadProject(), reloadTasks()]);
    } catch (err) {
      toast.error(err?.message || 'Failed to delete task');
    }
  };

  const handleGenerateMonthlyInvoice = async (send = false) => {
    if (!project?.id) return;
    try {
      setGeneratingMonthlyInvoice(true);
      const res = await graphqlRequest(GENERATE_MONTHLY_INVOICE, {
        projectId: project.seriesRootId || project.id,
        billingPeriod: currentBillingPeriod(),
        send,
      });
      const inv = res?.generateMonthlyProjectInvoice;
      if (!inv?.id) {
        toast.error('Could not create monthly invoice');
        return;
      }
      toast.success(
        send
          ? `Monthly invoice ${inv.invoiceNumber || ''} sent`
          : `Monthly invoice draft ${inv.invoiceNumber || ''} is ready`,
      );
      bumpRefresh();
      selectTab('Invoices');
    } catch (err) {
      toast.error(err?.message || 'Failed to create monthly invoice');
    } finally {
      setGeneratingMonthlyInvoice(false);
    }
  };

  const handleSaveTask = async (event) => {
    event?.preventDefault?.();
    if (savingTaskRef.current) return;
    if (!taskForm.title.trim()) {
      toast.error('Task title required');
      return;
    }
    if (!project?.id) {
      toast.error('Project not found');
      return;
    }
    if (!bookingId) {
      toast.error('No linked deal — tasks cannot be added from here.');
      return;
    }
    const serviceRows = projectServiceRows(project);
    if (serviceRows.length && !taskForm.serviceId) {
      toast.error('Add the task under a service');
      return;
    }
    const groupName = chosenGroupName(taskForm);
    if (!groupName) {
      toast.error('Group name is required');
      return;
    }
    savingTaskRef.current = true;
    try {
      setSavingTask(true);
      const input = {
        projectId: project.id,
        bookingId,
        title: taskForm.title.trim(),
        description: buildTaskDescription({
          serviceId: taskForm.serviceId,
          groupName,
          scope: taskForm.scope,
          instructions: taskForm.instructions,
        }),
      };
      if (taskForm.priority) input.priority = taskForm.priority;
      if (taskForm.date) input.date = taskForm.date;
      input.remainder = taskForm.serviceId ? `svc:${taskForm.serviceId}` : '';
      if (editingTask) {
        // The dropdown edits the primary person. Unassigned clears everyone;
        // a new person replaces the primary and keeps other rostered workers.
        const loadedIds = taskForm.assigneeIds || [];
        const selectedIds =
          taskForm.userId === (loadedIds[0] || '')
            ? loadedIds
            : taskForm.userId
              ? [taskForm.userId, ...loadedIds.slice(1).filter((id) => id !== taskForm.userId)]
              : [];
        const { projectId: _p, bookingId: _b, ...fields } = input;
        const update = { id: editingTask.id, ...fields };
        applyTaskAssignmentToInput(update, selectedIds, {
          isUpdate: true,
          assigneesLoaded: Boolean(taskForm.assigneesLoaded),
          previousIds: collectTaskAssigneeIds(editingTask),
        });
        await graphqlRequest(UPDATE_TASK, { input: update });
        toast.success('Task updated');
      } else {
        applyTaskAssignmentToInput(input, taskForm.userId ? [taskForm.userId] : [], {
          isUpdate: false,
        });
        await graphqlRequest(CREATE_TASK, { input });
        toast.success('Task created');
      }
      await Promise.all([reloadProject(), reloadTasks()]);
      setShowAddTask(false);
      setEditingTask(null);
      setTaskForm(emptyTaskForm());
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to save task');
    } finally {
      savingTaskRef.current = false;
      setSavingTask(false);
    }
  };

  const addTaskServiceRows = useMemo(
    () => projectServiceRows(project),
    [project],
  );

  const displayedTasks = useMemo(() => {
    const operational = uniqueTasks(projectTasks);
    if (operational.length) return operational;
    const real = Array.isArray(project?.tasks) ? project.tasks : [];
    if (real.length) return real;
    const snapshot = [];
    for (const row of project?.projectServices || []) {
      const serviceName = row?.service?.service || 'Service';
      for (const task of row?.tasks || []) {
        const title = String(task?.title || '').trim();
        if (!title) continue;
        snapshot.push({
          id: `snap-${row?.service?.id || 'svc'}-${title}`,
          title,
          status: 'pending',
          date: null,
          priority: null,
          description: task.description || serviceName,
        });
      }
    }
    return snapshot;
  }, [project, projectTasks]);

  const services = resolveProjectServices(project);

  const serviceNames = resolveProjectServiceNames(project);

  const currentStageId = booking?.stage?.id;
  const currentStageIndex = pipelineStages.findIndex((s) => s.id === currentStageId);
  const currentStageName =
    booking?.stage?.name ||
    (currentStageIndex >= 0 ? pipelineStages[currentStageIndex]?.name : null) ||
    '—';

  const frequency = resolveProjectFrequency(
    project,
    booking?.bookingFrequency,
    services.map((s) => s?.frequency),
    currentStageName !== '—' ? currentStageName : '',
  );

  const serviceLabel =
    serviceNames.length === 1
      ? serviceNames[0]
      : serviceNames.length > 1
        ? `${serviceNames.length} services`
        : frequency
          ? `${frequency} service`
          : 'Service package';

  const clientName = booking?.fullName || '—';
  const clientEmail = booking?.email || '—';
  const clientPhone = booking?.phone || '—';
  const clientAddress = booking?.address || '—';
  const projectSiteAddress = project?.projectAddress || clientAddress;
  const deliveryAddress = project?.deliveryAddress || '';
  const purchaseOrderNumber = project?.purchaseOrderNumber || '';
  const attention = project?.attention || '';

  const estimatedAmount = resolveProjectDisplayTotal(project);

  const dealRef = project?.projectId
    ? `D-${String(project.projectId).replace(/^P/i, '')}`
    : '—';

  const descriptionText = resolveProjectDescription(
    booking?.message,
    clientEmail,
  );

  const pipelineLabel = formatPipelineLabel(pipelineName);
  const showCustomInvoice = Boolean(bookingId);
  const isMonthlyContract =
    String(project?.billingCadence || '').toLowerCase() === 'monthly' &&
    !project?.seriesRootId;

  const serviceHistoryItems = useMemo(() => {
    const seriesFreq = formatRecurrenceFrequencyLabel(project?.recurrenceFrequency);
    if (services.length > 0) {
      const doneCount = progress.done;
      return services.map((svc, index) => ({
        id: svc.id || `svc-${index}`,
        name: svc.service?.service || `Service ${index + 1}`,
        frequency: seriesFreq || svc.frequency,
        done: index < doneCount,
      }));
    }
    const total = Math.max(progress.total, 1);
    return Array.from({ length: total }, (_, index) => ({
      id: `cycle-${index}`,
      name: `Service visit ${index + 1}`,
      frequency: seriesFreq,
      done: index < progress.done,
    }));
  }, [services, progress.done, progress.total, project?.recurrenceFrequency]);

  const scheduleRows = [
    { label: 'Frequency', value: frequency || 'Not set' },
    ...(isMonthlyContract
      ? [
          { label: 'Invoice timing', value: 'Monthly (end of month)' },
          {
            label: 'Monthly lines',
            value:
              String(project?.invoiceLineMode || '').toLowerCase() === 'task'
                ? 'Tasks'
                : 'Service package',
          },
          {
            label: 'Auto-send invoice',
            value: project?.autoSendMonthlyInvoice ? 'Yes' : 'Draft only',
          },
        ]
      : []),
    { label: 'Start Date', value: formatDate(project?.startDate) || '—' },
    {
      label: 'Deadline',
      value: project?.deadlineOngoing
        ? 'Ongoing (until the client stops)'
        : formatDate(project?.deadLine) || '—',
    },
    { label: 'Next Service', value: nextService },
    ...(booking?.preferredDate
      ? [{ label: 'Preferred Date', value: formatDate(booking.preferredDate) || booking.preferredDate }]
      : []),
    ...(project?.startTime || project?.endTime
      ? [{
          label: 'Service Window',
          value: [project.startTime, project.endTime].filter(Boolean).join(' – ') || '—',
        }]
      : []),
  ];

  const clientRows = [
    { label: 'Phone', value: clientPhone, href: clientPhone !== '—' ? `tel:${clientPhone}` : null },
    { label: 'Email', value: clientEmail, href: clientEmail !== '—' ? `mailto:${clientEmail}` : null },
    { label: 'Client Address', value: clientAddress },
    { label: 'Project Address', value: projectSiteAddress },
    ...(attention ? [{ label: 'Attention', value: attention }] : []),
    ...(deliveryAddress
      ? [{ label: 'Delivery Address', value: deliveryAddress }]
      : []),
    ...(purchaseOrderNumber
      ? [{ label: 'PO Number', value: purchaseOrderNumber }]
      : []),
    ...(booking?.postcode ? [{ label: 'Postcode', value: booking.postcode }] : []),
  ];

  if (loading) {
    return (
      <div className="project-detail">
        <button type="button" className="pd-back" onClick={onBack}>
          <FaArrowLeft /> Back to Projects
        </button>
        <div className="pd-loading">Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-detail">
        <button type="button" className="pd-back" onClick={onBack}>
          <FaArrowLeft /> Back to Projects
        </button>
        <div className="pd-empty">Project not found.</div>
      </div>
    );
  }

  return (
    <ProfileBookingProvider bookingId={bookingId} projectId={project?.id}>
      <div className="project-detail">
        <button type="button" className="pd-back" onClick={onBack}>
          <FaArrowLeft /> Back to Projects
        </button>

        <section className="pd-hero">
          <div className="pd-hero-top">
            <div className="pd-hero-left">
              <div className="pd-hero-meta">
                <span className="pd-project-id">{project.projectId || '—'}</span>
                <span className={statusDetailClass(displayStatus)}>{displayStatus}</span>
              </div>
              <h1 className="pd-title">{project.name}</h1>
              <p className="pd-subtitle">
                Client:{' '}
                <strong>{clientName}</strong>
                {' · '}
                Deal ref: <strong>{dealRef}</strong>
              </p>
            </div>
            <div className="pd-hero-price">
              <div className="pd-price-value">
                {estimatedAmount != null
                  ? formatAUD(estimatedAmount)
                  : '—'}
              </div>
              <div className="pd-price-label">{serviceLabel}</div>
            </div>
          </div>

          {pipelineStages.length > 0 && (
            <div className="pd-pipeline-block">
              <div className="pd-pipeline-head">
                <span>Pipeline{pipelineName ? `: ${pipelineName}` : ''}</span>
                <strong>{currentStageName}</strong>
              </div>
              <div className="pd-stage-progress">
                {pipelineStages.map((stage, idx) => (
                  <React.Fragment key={stage.id}>
                    <div
                      className={`pd-stage-dot ${
                        currentStageIndex >= 0 && idx <= currentStageIndex ? 'done' : ''
                      }`}
                      title={stage.name}
                    >
                      {currentStageIndex >= 0 && idx <= currentStageIndex ? (
                        <FaCheck aria-hidden />
                      ) : (
                        idx + 1
                      )}
                    </div>
                    {idx !== pipelineStages.length - 1 && (
                      <div
                        className={`pd-stage-line ${
                          currentStageIndex >= 0 && idx < currentStageIndex ? 'filled' : ''
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          <div className="pd-progress-block">
            <div className="pd-progress-head">
              <span>Overall progress</span>
              <strong>{progress.percent}%</strong>
            </div>
            <div className="pd-progress-bar">
              <div
                className="pd-progress-fill"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
            <p className="pd-progress-foot">
              {progress.done} of {progress.total}{' '}
              {(project?.tasks || []).length > 0 ? 'tasks' : 'services'} completed
              this cycle
            </p>
          </div>
        </section>

        <nav className="pd-tabs" aria-label="Project sections">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={`pd-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => selectTab(tab)}
            >
              {tab}
            </button>
          ))}
        </nav>

        <section className="pd-panel">
          {activeTab === 'Overview' && (
            <>
            <div className="pd-overview-grid">
              <div className="pd-card pd-info-card">
                <section className="pd-detail-section">
                  <h4 className="pd-detail-label">Client</h4>
                  <div className="pd-client-header">
                    <span className="pd-client-avatar">
                      {String(clientName).charAt(0).toUpperCase()}
                    </span>
                    <div className="pd-client-heading">
                      <p className="pd-client-name">{clientName}</p>
                    </div>
                  </div>
                  <dl className="pd-info-rows">
                    {clientRows.map((row) => (
                      <div key={row.label} className="pd-info-row">
                        <dt>{row.label}</dt>
                        <dd>
                          {row.href ? (
                            <a href={row.href} className="pd-info-link">
                              {row.value}
                            </a>
                          ) : (
                            row.value
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>

                <section className="pd-detail-section">
                  <h4 className="pd-detail-label">Schedule</h4>
                  <dl className="pd-info-rows">
                    {scheduleRows.map((row) => (
                      <div key={row.label} className="pd-info-row">
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                  {isMonthlyContract ? (
                    <button
                      type="button"
                      className="pd-custom-create-btn"
                      disabled={generatingMonthlyInvoice}
                      onClick={() => handleGenerateMonthlyInvoice(false)}
                      style={{ marginTop: 12 }}
                    >
                      {generatingMonthlyInvoice
                        ? 'Creating invoice…'
                        : 'Invoice this month'}
                    </button>
                  ) : null}
                </section>

                <section className="pd-detail-section">
                  <div className="pd-history-head">
                    <h4 className="pd-detail-label">Service History</h4>
                    <span className="pd-history-count">
                      {progress.done} of {progress.total} completed
                    </span>
                  </div>
                  {serviceHistoryItems.length > 0 ? (
                    <ul className="pd-history-list">
                      {serviceHistoryItems.map((item) => (
                        <li
                          key={item.id}
                          className={`pd-history-item ${item.done ? 'done' : 'pending'}`}
                        >
                          <span
                            className={`pd-history-dot ${item.done ? 'done' : 'pending'}`}
                            aria-hidden
                          >
                            {item.done ? <FaCheck /> : <FaClock />}
                          </span>
                          <div className="pd-history-body">
                            <strong>{item.name}</strong>
                            <span>
                              {item.done ? 'Completed' : 'Pending'}
                              {item.frequency ? ` · ${item.frequency}` : ''}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="pd-empty-hint">No service visits recorded yet.</p>
                  )}
                </section>
              </div>

              <div className="pd-card pd-detail-card">
                <section className="pd-detail-section">
                  <h4 className="pd-detail-label">Description</h4>
                  {descriptionText ? (
                    <p className="pd-description">{descriptionText}</p>
                  ) : (
                    <p className="pd-empty-hint">
                      No description provided for this project yet.
                    </p>
                  )}
                </section>

                <section className="pd-detail-section">
                  <h4 className="pd-detail-label">Required Services</h4>
                  {serviceNames.length > 0 ? (
                    <ul className="pd-service-tags">
                      {services.map((svc, index) => {
                        const tagFreq =
                          formatRecurrenceFrequencyLabel(project?.recurrenceFrequency) ||
                          svc.frequency;
                        return (
                          <li key={svc.id || svc.service?.id || `svc-${index}`} className="pd-service-tag">
                            <span>{svc.service?.service || 'Service'}</span>
                            {tagFreq ? <small>{tagFreq}</small> : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="pd-empty-hint">No services linked to this deal yet.</p>
                  )}
                </section>

                {currentStageName !== '—' && (
                  <section className="pd-detail-section">
                    <h4 className="pd-detail-label">Deal Stage</h4>
                    <div className="pd-deal-stage">
                      <span className="pd-stage-badge">{currentStageName}</span>
                      {pipelineLabel ? (
                        <span className="pd-pipeline-name">{pipelineLabel}</span>
                      ) : null}
                    </div>
                  </section>
                )}
              </div>
            </div>
            {showCustomInvoice ? (
              <div className="pd-card pd-card--full pd-custom-invoice-wrap">
                <div className="pd-custom-callout">
                  <div>
                    <strong>Custom invoices</strong>
                    <p>
                      Extra jobs billed separately from the regular visit.
                      Open the Invoices tab to see regular and custom invoices
                      together, or create one here.
                    </p>
                  </div>
                  <div className="pd-custom-callout-actions">
                    <CustomInvoicePanel
                      variant="create-only"
                      bookingId={bookingId}
                      projectId={project.id}
                      clientEmail={booking?.email || ''}
                    />
                    <button
                      type="button"
                      className="pd-custom-btn"
                      onClick={() => selectTab('Invoices')}
                    >
                      View all invoices
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            </>
          )}

          {activeTab === 'Tasks' && (
            <div className="pd-card pd-card--full">
              <div className="pd-card-head">
                <h3>Tasks</h3>
                <button
                  type="button"
                  className="pd-add-task-btn"
                  onClick={openAddTask}
                >
                  ＋ Add task
                </button>
              </div>
              {(displayedTasks || []).length === 0 ? (
                <p className="pd-muted">No tasks assigned to this project yet.</p>
              ) : (
                <ul className="pd-task-list">
                  {displayedTasks.map((task) => {
                    const snapshot = String(task.id || '').startsWith('snap-');
                    const { groupName, scope, instructions } = parseTaskMeta(task);
                    return (
                      <li key={task.id} className="pd-task-item">
                        <div className="pd-task-main">
                          {groupName ? (
                            <small className="pd-task-group">{groupName}</small>
                          ) : null}
                          <strong>{task.title}</strong>
                          {scope ? <p className="pd-task-scope">{scope}</p> : null}
                          {instructions ? (
                            <p className="pd-task-instructions">
                              <b>Internal instructions:</b> {instructions}
                            </p>
                          ) : null}
                          <span className="pd-muted">
                            {snapshot ? 'From quotation' : formatTaskAssigneeNames(task) === '—' ? 'Unassigned' : formatTaskAssigneeNames(task)}
                            {' · '}
                            {task.date ? formatDate(task.date) : 'No date'}
                            {task.priority ? ` · ${task.priority}` : ''}
                          </span>
                        </div>
                        <div className="pd-task-side">
                          <span className={statusDetailClass(mapStatusToDisplay(task.status))}>
                            {mapStatusToDisplay(task.status)}
                          </span>
                          {snapshot ? null : (
                            <>
                              <button
                                type="button"
                                className="pd-task-edit"
                                onClick={() => openEditTask(task)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="pd-task-delete"
                                onClick={() => handleDeleteTask(task.id)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {activeTab === 'Team' && (
            <div className="pd-card pd-card--full">
              <h3>Team</h3>
              {team.length === 0 ? (
                <p className="pd-muted">No team members assigned yet.</p>
              ) : (
                <ul className="pd-team-list">
                  {team.map((member) => (
                    <li key={member.id}>
                      <span className="pd-team-avatar">{member.initials}</span>
                      <span>{member.name || 'Team member'}</span>
                    </li>
                  ))}
                </ul>
              )}
              {project.manager && (
                <p className="pd-muted pd-manager-line">
                  Manager:{' '}
                  {`${project.manager.firstName || ''} ${project.manager.lastName || ''}`.trim() ||
                    project.manager.email}
                </p>
              )}
            </div>
          )}

          {activeTab === 'Files' ? (
            <ProjectFilesPanel bookingId={bookingId} projectId={project?.id} />
          ) : null}

          {activeTab === 'Client sharing' && <ClientReportSharing projectId={project.id} />}
          {activeTab === 'Invoices' && (
  <div className="pd-card pd-card--full pd-embedded-tab">
    <div className="pd-card-head">
      <h3>Invoices</h3>
      {bookingId ? (
        <button
          type="button"
          className="pd-add-task-btn"
          onClick={() => setShowInvoiceSettings(true)}
        >
          ⚙ Invoice Setting
        </button>
      ) : null}
    </div>
    {bookingId ? (
      <Invoice schedule={invoiceSchedule} />
    ) : (
      <p className="pd-muted">No linked deal — invoices unavailable.</p>
    )}
  </div>
)}

          {activeTab === 'Activity' && (
            <div className="pd-card pd-card--full pd-embedded-tab">
              <h3>Activity</h3>
              {bookingId ? (
                <History />
              ) : (
                <p className="pd-muted">
                  Project created on {formatDate(project.createdAt) || '—'}.
                </p>
              )}
            </div>
          )}
        </section>
      </div>
      {showAddTask &&
        createPortal(
          <div
            className="pd-task-overlay"
            onClick={(event) => {
              if (event.target === event.currentTarget) closeAddTask();
            }}
            role="presentation"
          >
            <form
              className="pd-task-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pd-add-task-title"
              onClick={(event) => event.stopPropagation()}
              onSubmit={handleSaveTask}
            >
              <div className="pd-task-modal-head">
                <div>
                  <h2 id="pd-add-task-title">
                    {editingTask ? 'Update task' : 'Add task'}
                  </h2>
                  <p>
                    {editingTask
                      ? 'Update this task on the project'
                      : 'Adds this task to the project only. The service catalogue is left unchanged.'}
                  </p>
                </div>
                <button
                  type="button"
                  className="pd-task-close"
                  onClick={closeAddTask}
                  aria-label="Close"
                  disabled={savingTask}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="pd-task-grid">
                {addTaskServiceRows.length > 0 ? (
                  <label className="pd-task-field pd-task-field--wide">
                    Service
                    <select
                      value={taskForm.serviceId}
                      onChange={(e) =>
                        setTaskForm({ ...taskForm, serviceId: e.target.value })
                      }
                      required
                    >
                      {addTaskServiceRows.map((row) => (
                        <option key={row.catalogId} value={row.catalogId}>
                          {row.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label className="pd-task-field pd-task-field--wide">
                  Existing groups
                  <select
                    value={
                      groupNamesForService(
                        project,
                        displayedTasks,
                        taskForm.serviceId,
                      ).find(
                        (name) =>
                          name.toLowerCase() ===
                          String(taskForm.groupName || '').trim().toLowerCase(),
                      ) || ''
                    }
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, groupName: e.target.value })
                    }
                  >
                    <option value="">Choose an existing group</option>
                    {groupNamesForService(
                      project,
                      displayedTasks,
                      taskForm.serviceId,
                    ).map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="pd-task-field pd-task-field--wide">
                  Group name
                  <input
                    value={taskForm.groupName}
                    placeholder="Type a group name"
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, groupName: e.target.value })
                    }
                    required
                  />
                </label>
                <label className="pd-task-field pd-task-field--wide">
                  Task
                  <input
                    value={taskForm.title}
                    placeholder="e.g. Deep-clean and sanitise the bathroom"
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, title: e.target.value })
                    }
                    required
                  />
                </label>
                <label className="pd-task-field pd-task-field--wide">
                  Client-facing scope
                  <textarea
                    rows={3}
                    value={taskForm.scope}
                    placeholder="What the client sees for this task"
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, scope: e.target.value })
                    }
                  />
                </label>
                <label className="pd-task-field pd-task-field--wide">
                  Internal work instructions
                  <textarea
                    rows={3}
                    value={taskForm.instructions}
                    placeholder="Notes for the team on this project"
                    onChange={(e) =>
                      setTaskForm({
                        ...taskForm,
                        instructions: e.target.value,
                      })
                    }
                  />
                </label>
                <label className="pd-task-field">
                  Deadline
                  <input
                    type="date"
                    value={taskForm.date}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, date: e.target.value })
                    }
                  />
                </label>
                <label className="pd-task-field">
                  Priority
                  <select
                    value={taskForm.priority}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, priority: e.target.value })
                    }
                  >
                    <option value="">Select</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </label>
                <label className="pd-task-field">
                  Assigned to
                  <select
                    value={taskForm.userId}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, userId: e.target.value })
                    }
                  >
                    <option value="">Unassigned</option>
                    {managers.map((user) => {
                      const fullName =
                        `${user.firstName || ''} ${user.lastName || ''}`.trim();
                      return (
                        <option key={user.id} value={user.id}>
                          {fullName || user.email}
                        </option>
                      );
                    })}
                    {taskForm.userId &&
                    !managers.some((user) => user.id === taskForm.userId) ? (
                      <option value={taskForm.userId}>
                        {formatTaskAssigneeNames(editingTask || {}) === '—'
                          ? 'Assigned'
                          : formatTaskAssigneeNames(editingTask)}
                      </option>
                    ) : null}
                  </select>
                </label>
              </div>
              <div className="pd-task-actions">
                <button
                  type="button"
                  className="pd-task-cancel"
                  onClick={closeAddTask}
                  disabled={savingTask}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pd-task-submit"
                  disabled={savingTask}
                >
                  {savingTask
                    ? 'Saving…'
                    : editingTask
                      ? 'Save changes'
                      : 'Add task'}
                </button>
              </div>
            </form>
          </div>,
          document.body,
        )}

        <InvoiceSettingsDrawer
  open={showInvoiceSettings}
  onClose={() => !invoiceSettings.saving && setShowInvoiceSettings(false)}
  onSave={handleSaveInvoiceSettings}
  saving={invoiceSettings.saving}
  projectTitle={project.name}
  projectCode={project.projectId}
  initialSettings={invoiceSettings.initialSettings}
  sentDates={invoiceSettings.sentDates}
  invoiceData={invoiceSettings.invoiceData}
  profile={invoiceSettings.profile}
/>
    </ProfileBookingProvider>
  );
}

